import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PREVENTIVE_TASK_EXECUTION_PATH } from '../../../../config/paths';

import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';

import Content from '../../../layout/Content';

import { postPreventiveTaskExecution } from '../../../../actions/task-schedule/PreventiveTaskExecution';

import PreventiveTaskExecutionForm from './PreventiveTaskExecutionForm';

import {
  PREVENTIVE_TASK_EXECUTION_STATUS,
  RESOURCE_EXECUTION_MODE,
} from '../../../../config/constants';

import { tzNormalizeDate } from '../../../../utils/date';

const getSelectValue = (field) => {
  if (field && typeof field === 'object') {
    return field.value;
  }

  return field;
};

const normalizeFile = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof File) {
    return value;
  }

  if (Array.isArray(value)) {
    return normalizeFile(value[0]);
  }

  if (value.length) {
    return value[0];
  }

  if (value.file instanceof File) {
    return value.file;
  }

  if (value.value instanceof File) {
    return value.value;
  }

  return null;
};

const toNullableValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return value;
};

const normalizeDate = (date) => {
  if (!date) {
    return null;
  }

  return tzNormalizeDate({ date });
};

const calculateGroupedDateRange = (resourceGroups) => {
  const groups = Array.isArray(resourceGroups) ? resourceGroups : [];

  const startedDates = groups
    .map((group) => group && group.startedDate)
    .filter(Boolean);

  const completedDates = groups
    .map((group) => group && group.completedDate)
    .filter(Boolean);

  const startedDate = startedDates.length
    ? startedDates.reduce((earliest, current) =>
        new Date(current).getTime() < new Date(earliest).getTime()
          ? current
          : earliest,
      )
    : null;

  const completedDate = completedDates.length
    ? completedDates.reduce((latest, current) =>
        new Date(current).getTime() > new Date(latest).getTime()
          ? current
          : latest,
      )
    : null;

  return {
    startedDate,
    completedDate,
  };
};

const buildGroupedResourceExecutions = (formValues, executionStatus) => {
  const groups = Array.isArray(formValues.resourceGroups)
    ? formValues.resourceGroups
    : [];

  return groups.flatMap((group) => {
    const currentGroup = group || {};

    const resources = Array.isArray(currentGroup.resources)
      ? currentGroup.resources
      : [];

    return resources
      .map((resource) => {
        const resourceId = getSelectValue(resource);

        if (
          resourceId === undefined ||
          resourceId === null ||
          resourceId === ''
        ) {
          return null;
        }

        return {
          resourceId,

          startedDate: normalizeDate(currentGroup.startedDate),

          completedDate: normalizeDate(currentGroup.completedDate),

          status: getSelectValue(currentGroup.status) || executionStatus,

          comment: currentGroup.comment || null,
        };
      })
      .filter(Boolean);
  });
};

const buildLegacyResourceExecutions = (
  formValues,
  isSameDateMode,
  executionStatus,
) => {
  const resources = Array.isArray(formValues.resources)
    ? formValues.resources
    : [];

  return resources
    .filter((item) => item && getSelectValue(item.resourceId))
    .map((item) => ({
      resourceId: getSelectValue(item.resourceId),

      startedDate: isSameDateMode
        ? normalizeDate(formValues.startedDate)
        : normalizeDate(item.startedDate),

      completedDate: isSameDateMode
        ? normalizeDate(formValues.completedDate)
        : normalizeDate(item.completedDate),

      status: isSameDateMode ? executionStatus : getSelectValue(item.status),

      comment: isSameDateMode
        ? formValues.comment || null
        : item.comment || null,
    }));
};

export const buildPreventiveTaskExecutionBody = (formValues) => {
  const selectedExecutionMode = getSelectValue(
    formValues.resourceExecutionMode,
  );

  const executionStatus = getSelectValue(formValues.status);

  const attachedFile = normalizeFile(formValues.attachedFile);

  const isGroupedMode =
    selectedExecutionMode === RESOURCE_EXECUTION_MODE.GROUPED.value;

  const isSameDateMode =
    selectedExecutionMode === RESOURCE_EXECUTION_MODE.SAME_DATE.value;

  const groupedDateRange = isGroupedMode
    ? calculateGroupedDateRange(formValues.resourceGroups)
    : null;

  const startedDate = isGroupedMode
    ? groupedDateRange.startedDate
    : formValues.startedDate;

  const completedDate = isGroupedMode
    ? groupedDateRange.completedDate
    : formValues.completedDate;

  const resources = isGroupedMode
    ? buildGroupedResourceExecutions(formValues, executionStatus)
    : buildLegacyResourceExecutions(
        formValues,
        isSameDateMode,
        executionStatus,
      );

  const resourceExecutionMode = resources.length
    ? RESOURCE_EXECUTION_MODE.RESOURCE_BASED.value
    : RESOURCE_EXECUTION_MODE.SAME_DATE.value;

  return {
    preventiveTaskId: getSelectValue(formValues.preventiveTaskId),

    preventiveTaskProjectionId: getSelectValue(
      formValues.preventiveTaskProjectionId,
    ),

    scheduleDate: normalizeDate(formValues.scheduleDate),

    startedDate: normalizeDate(startedDate),

    completedDate: normalizeDate(completedDate),

    status: executionStatus,

    resourceExecutionMode,

    executorUserId: getSelectValue(formValues.executorUserId),

    comment: formValues.comment || null,

    observation: formValues.observation || null,

    attachedFile,

    spentAmount: toNullableValue(formValues.spentAmount),

    resources,
  };
};

const initialValues = {
  status: PREVENTIVE_TASK_EXECUTION_STATUS.DONE,

  resourceExecutionMode: RESOURCE_EXECUTION_MODE.GROUPED,

  resources: [],

  resourceGroups: [
    {
      startedDate: null,
      completedDate: null,

      status: PREVENTIVE_TASK_EXECUTION_STATUS.DONE,

      resources: [],
      comment: null,
    },
  ],
};

const NewPreventiveTaskExecution = ({
  breadcrumbs,
  loading,
  dispatchPostPreventiveTaskExecution,
}) => {
  const onSubmit = (formValues) => {
    const body = buildPreventiveTaskExecutionBody(formValues);

    dispatchPostPreventiveTaskExecution(body);
  };

  const content = (
    <PreventiveTaskExecutionForm
      onSubmit={onSubmit}
      initialValues={initialValues}
      loading={loading}
    />
  );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Registrar Ejecución Programada"
      subtitle="Registrar ejecución inicial, manual o desde proyección"
      content={content}
    />
  );
};

NewPreventiveTaskExecution.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,

  loading: PropTypes.bool,

  dispatchPostPreventiveTaskExecution: PropTypes.func.isRequired,
};

NewPreventiveTaskExecution.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchPostPreventiveTaskExecution: postPreventiveTaskExecution,
};

const mapStateToProps = ({ TaskScheduleUnit }) => ({
  breadcrumbs: [
    ...TaskScheduleUnit.UnitHome.get('breadcrumbs'),

    {
      text: 'Ejecuciones Programadas',
      href: PREVENTIVE_TASK_EXECUTION_PATH,
    },

    {
      text: 'Nuevo',
      href: '',
    },
  ],

  loading: !TaskScheduleUnit.PreventiveTaskExecution.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(NewPreventiveTaskExecution);
